/**
 * @fileoverview Board-scoped dataset payload lifecycle.
 *
 * Single owner of "which datasets do we have downloaded into DuckDB right
 * now?" Board routes call `mountBoard` / `unmountBoard` and the sidebar
 * calls `prefetchBoard` / `prefetchDataset`. Everything else (rift queries,
 * filter computations) calls the canonical action API
 * `ensureDatasetPayload(id, signal)` and piggybacks on the same payload key.
 *
 * Cancellation: each mounted board owns one `AbortController` per dataset
 * it references. When the board unmounts, those controllers abort local waiters.
 * Fetch dedupe/caching is handled by TanStack Query, so a fetch already started
 * for the same payload key may continue if other consumers still need it.
 */

import { logger } from "~/libs/logger";
import { ensureDatasetPayload } from "~/stores/datasets/datasets.actions";
import { isExpectedPayloadError } from "~/stores/datasets/rift-dataset-payload.query";
import { blocks$ } from "../blocks/blocks.crud";

/** Datasets currently kept "warm" by an active board, with their abort controllers. */
const activeRefs = new Map<string, Map<string, AbortController>>();
// shape: datasetId -> (boardId -> controller)

/** Boards currently mounted (vs. just prefetched). */
const mountedBoards = new Set<string>();

/** Pending prefetches by boardId, keyed for cancel-on-replace. */
const prefetchControllers = new Map<string, AbortController>();

/** Pending prefetches by datasetId. */
const datasetPrefetchControllers = new Map<string, AbortController>();

/** Window after which an unpromoted prefetch is auto-cancelled. */
const PREFETCH_CANCEL_AFTER_MS = 30_000;

/**
 * Logs a payload-warm failure at the appropriate level. The warm is a
 * best-effort optimization — the on-demand consumer path re-fetches and
 * surfaces real failures — so expected/recoverable conditions (abort,
 * missing/unpublished payload, transient network/timeout) are logged at
 * `debug` and only genuinely unexpected failures escalate to `error`.
 */
const logPayloadWarmFailure = (
  err: unknown,
  context: { datasetId: string; boardId?: string },
): void => {
  if (isExpectedPayloadError(err)) {
    logger.debug({ err, ...context }, "dataset payload warm skipped");
    return;
  }
  logger.error({ err, ...context }, "dataset payload warm failed");
};

/**
 * True when at least one mounted board still holds a live payload reference
 * for `datasetId`. Used to decide whether a newly published payload should be
 * downloaded in the background before adopting its etag (so on-screen blocks
 * keep rendering the current data instead of a skeleton).
 */
export const hasActiveBoardRefs = (datasetId: string): boolean => {
  const perDataset = activeRefs.get(datasetId);
  if (!perDataset) return false;
  for (const [boardId, controller] of perDataset) {
    if (!mountedBoards.has(boardId)) continue;
    if (!controller.signal.aborted) return true;
  }
  return false;
};

/** Computes the unique datasetIds referenced by blocks on a given board. */
const getBoardDatasetIds = (boardId: string): Set<string> => {
  const all = blocks$.get() ?? {};
  const ids = new Set<string>();
  for (const block of Object.values(all)) {
    if (block?.boardId === boardId && block.datasetId) {
      ids.add(block.datasetId);
    }
  }
  return ids;
};

/**
 * Begins a payload load for `datasetId` on behalf of `boardId`. If a load is
 * already in flight (from another board, or this same board), the existing
 * controller is reused. Returns the controller so the caller can abort it.
 */
const acquire = (datasetId: string, boardId: string): AbortController => {
  let perDataset = activeRefs.get(datasetId);
  if (!perDataset) {
    perDataset = new Map();
    activeRefs.set(datasetId, perDataset);
  }
  const existing = perDataset.get(boardId);
  if (existing && !existing.signal.aborted) return existing;

  const controller = new AbortController();
  perDataset.set(boardId, controller);

  ensureDatasetPayload(datasetId, controller.signal).catch((err: unknown) => {
    if (controller.signal.aborted) return;
    logPayloadWarmFailure(err, { datasetId, boardId });
  });

  return controller;
};

/** Releases this board's reference to `datasetId`. Aborts the load if no other board still holds a ref. */
const release = (datasetId: string, boardId: string) => {
  const perDataset = activeRefs.get(datasetId);
  if (!perDataset) return;
  const controller = perDataset.get(boardId);
  if (!controller) return;
  perDataset.delete(boardId);
  controller.abort();
  if (perDataset.size === 0) {
    activeRefs.delete(datasetId);
  }
};

/**
 * Starts loading every dataset referenced by the active board's blocks.
 * Subsequent calls for the same boardId are idempotent.
 *
 * If a prefetch was in flight for this board, it is "promoted" to a mount —
 * its controllers are kept and the cancel timer is cleared.
 */
export const mountBoard = (boardId: string): void => {
  if (!boardId) return;

  const prefetch = prefetchControllers.get(boardId);
  if (prefetch) {
    prefetchControllers.delete(boardId);
    // The prefetch controllers are already keyed under boardId in activeRefs,
    // so simply marking the board as mounted is enough — they stay alive.
  }

  mountedBoards.add(boardId);

  const datasetIds = getBoardDatasetIds(boardId);
  for (const id of datasetIds) {
    acquire(id, boardId);
  }
};

/**
 * Releases this board's references and aborts any in-flight loads it was
 * waiting on. Safe to call for a board that was never mounted.
 */
export const unmountBoard = (boardId: string): void => {
  if (!boardId) return;
  mountedBoards.delete(boardId);

  const prefetch = prefetchControllers.get(boardId);
  if (prefetch) {
    prefetchControllers.delete(boardId);
  }

  const refs: Array<[string, AbortController]> = [];
  for (const [datasetId, perDataset] of activeRefs) {
    const c = perDataset.get(boardId);
    if (c) refs.push([datasetId, c]);
  }
  for (const [datasetId] of refs) {
    release(datasetId, boardId);
  }
};

/**
 * Begins a low-priority load for `boardId`'s datasets without marking the
 * board as mounted. If `mountBoard(boardId)` doesn't follow within
 * `PREFETCH_CANCEL_AFTER_MS`, the prefetched payloads are released.
 *
 * Idempotent — repeated prefetches for the same board reset the cancel timer.
 */
export const prefetchBoard = (boardId: string): void => {
  if (!boardId) return;
  if (mountedBoards.has(boardId)) return;

  const existing = prefetchControllers.get(boardId);
  if (existing && !existing.signal.aborted) {
    return;
  }

  const datasetIds = getBoardDatasetIds(boardId);
  if (datasetIds.size === 0) return;

  const controller = new AbortController();
  prefetchControllers.set(boardId, controller);

  for (const id of datasetIds) {
    acquire(id, boardId);
  }

  setTimeout(() => {
    if (mountedBoards.has(boardId)) return;
    if (prefetchControllers.get(boardId) !== controller) return;
    prefetchControllers.delete(boardId);
    for (const id of getBoardDatasetIds(boardId)) {
      release(id, boardId);
    }
  }, PREFETCH_CANCEL_AFTER_MS);
};

/**
 * Begins a low-priority load for a single dataset (e.g. on sidebar hover).
 * Reuses any in-flight load. Auto-cancels after the prefetch window unless a
 * board mount picks it up first.
 */
export const prefetchDataset = (datasetId: string): void => {
  if (!datasetId) return;

  const existing = datasetPrefetchControllers.get(datasetId);
  if (existing && !existing.signal.aborted) return;

  const key = `__prefetch__${datasetId}`;
  const controller = acquire(datasetId, key);
  datasetPrefetchControllers.set(datasetId, controller);

  setTimeout(() => {
    if (datasetPrefetchControllers.get(datasetId) !== controller) return;
    datasetPrefetchControllers.delete(datasetId);
    release(datasetId, key);
  }, PREFETCH_CANCEL_AFTER_MS);
};

/**
 * Re-warms `datasetId`'s payload for every mounted board that references it,
 * reusing each board's existing abort controller so an unmount cancels the
 * re-warm. Called from `datasets.crud.ts` on a real `etag` transition so the
 * new payload version is fetched in the background. No-op when no mounted
 * board references the dataset.
 */
export const rewarmDataset = (datasetId: string): void => {
  const perDataset = activeRefs.get(datasetId);
  if (!perDataset || perDataset.size === 0) return;
  for (const [boardId, controller] of perDataset) {
    if (!mountedBoards.has(boardId)) continue;
    if (controller.signal.aborted) continue;
    ensureDatasetPayload(datasetId, controller.signal).catch((err: unknown) => {
      if (controller.signal.aborted) return;
      logPayloadWarmFailure(err, { datasetId, boardId });
    });
  }
};

/**
 * Re-evaluates a mounted board's referenced datasets — call when a block on
 * the board changes its `datasetId` so we acquire/release accordingly.
 */
export const reconcileBoardRefs = (boardId: string): void => {
  if (!mountedBoards.has(boardId)) return;
  const wanted = getBoardDatasetIds(boardId);
  const current = new Set<string>();
  for (const [datasetId, perDataset] of activeRefs) {
    if (perDataset.has(boardId)) current.add(datasetId);
  }
  for (const id of wanted) {
    if (!current.has(id)) acquire(id, boardId);
  }
  for (const id of current) {
    if (!wanted.has(id)) release(id, boardId);
  }
};
